<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  value?: number
  max?: number
  buffer?: number
  indeterminate?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: 'primary' | 'success' | 'warning' | 'danger'
  showLabel?: boolean
  label?: string
}

const props = withDefaults(defineProps<Props>(), {
  value: 0,
  max: 100,
  buffer: 0,
  size: 'md',
  variant: 'primary',
  showLabel: false,
  indeterminate: false,
})

const percent = computed(() => {
  if (props.indeterminate) return 100
  const max = props.max || 100
  return Math.max(0, Math.min(100, (props.value / max) * 100))
})

const bufferPercent = computed(() => {
  if (props.indeterminate) return 0
  const max = props.max || 100
  return Math.max(0, Math.min(100, (props.buffer / max) * 100))
})

const heightClass = computed(() => {
  switch (props.size) {
    case 'sm': return 'h-1'
    case 'lg': return 'h-3'
    default:   return 'h-1.5'
  }
})

const variantClass = computed(() => {
  switch (props.variant) {
    case 'success': return 'bg-success'
    case 'warning': return 'bg-warning'
    case 'danger':  return 'bg-danger'
    default:        return 'bg-brand-500'
  }
})
</script>

<template>
  <div class="w-full">
    <div
      class="relative w-full overflow-hidden rounded-full bg-bg-elevated"
      :class="heightClass"
      role="progressbar"
      :aria-valuenow="indeterminate ? undefined : value"
      :aria-valuemin="0"
      :aria-valuemax="max"
    >
      <!-- Buffer -->
      <div
        v-if="!indeterminate && buffer > 0"
        class="absolute inset-y-0 left-0 bg-brand-500/20"
        :style="{ width: bufferPercent + '%' }"
      />
      <!-- Value -->
      <div
        v-if="!indeterminate"
        class="relative h-full rounded-full transition-all duration-base"
        :class="variantClass"
        :style="{ width: percent + '%' }"
      />
      <!-- Indeterminate -->
      <div
        v-else
        class="absolute inset-y-0 left-0 w-1/3 rounded-full"
        :class="variantClass"
      >
        <div
          class="h-full w-full rounded-full opacity-50"
          :class="variantClass"
        />
        <div class="absolute inset-0 animate-progress-indeterminate rounded-full" :class="variantClass" />
      </div>
    </div>
    <div
      v-if="showLabel || label"
      class="mt-1 flex items-center justify-between text-xs text-fg-muted"
    >
      <span>{{ label }}</span>
      <span>{{ indeterminate ? '...' : Math.round(percent) + '%' }}</span>
    </div>
  </div>
</template>

<style scoped>
@keyframes progress-indeterminate {
  0%   { left: -33%; }
  100% { left: 100%; }
}
.animate-progress-indeterminate {
  animation: progress-indeterminate 1.4s ease-in-out infinite;
}
</style>
